import React, { useState } from "react";
import { Alert, Col, Container, Row } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";

import Breadcrumbs from "../../../../Common/Breadcrumbs";
import ShimmerLoader from "../../../../Common/ShimmerLoader";
import { StatCard } from "../../../../components/Ads/StatCard/StatCard";
import { useAdPerformance } from "../../hooks";

import "./AdPerformance.css";

type Preset = "7d" | "30d" | "90d" | "custom";

function toYMD(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function dateFromOffset(offsetDays: number): string {
  const d = new Date();
  d.setDate(d.getDate() - offsetDays);
  return toYMD(d);
}

function formatDate(isoDate: string): string {
  if (!isoDate) return "-";
  const [y, m, d] = isoDate.split("-");
  return `${d}/${m}/${y}`;
}

const formatNum = (value: number) => value.toLocaleString("en-IN");

const AdPerformancePage: React.FC = () => {
  const { id: adId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [preset, setPreset] = useState<Preset>("30d");

  const {
    analytics,
    loading,
    error,
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    fetchAnalytics,
  } = useAdPerformance(adId ?? "");

  const handlePreset = (p: Preset) => {
    setPreset(p);
    if (p === "7d") {
      setStartDate(dateFromOffset(6));
      setEndDate(toYMD(new Date()));
    } else if (p === "30d") {
      setStartDate(dateFromOffset(29));
      setEndDate(toYMD(new Date()));
    } else if (p === "90d") {
      setStartDate(dateFromOffset(89));
      setEndDate(toYMD(new Date()));
    }
  };

  const handleDateChange = (field: "start" | "end", value: string) => {
    setPreset("custom");
    if (field === "start") setStartDate(value);
    else setEndDate(value);
  };

  const summary = analytics?.summary;
  const daily = analytics?.daily ?? [];

  const statCards = [
    {
      title: "Impressions",
      value: summary ? formatNum(summary.impressions) : "—",
      iconClassName: "ph-eye",
    },
    {
      title: "Clicks",
      value: summary ? formatNum(summary.clicks) : "—",
      iconClassName: "ph-hand-pointing",
      iconBgColor: "#FFF1F2",
      iconColor: "#F43F5E",
    },
    {
      title: "CTR",
      value: summary ? `${summary.ctr.toFixed(2)}%` : "—",
      iconClassName: "ph-target",
    },
    {
      title: "Conversions",
      value: summary ? formatNum(summary.conversions) : "—",
      iconClassName: "ph-check-circle",
      iconBgColor: "#F0FDF4",
      iconColor: "#22C55E",
    },
    {
      title: "Conversion Rate",
      value: summary ? `${summary.conversion_rate.toFixed(2)}%` : "—",
      iconClassName: "ph-percent",
      iconBgColor: "#FFF7ED",
      iconColor: "#F97316",
    },
  ];

  return (
    <div className="page-content">
      <Container fluid>
        <div className="mb-3" style={{ padding: "0 15px" }}>
          <Breadcrumbs
            items={[
              { label: "Ads", onClick: () => navigate("/ads/overview") },
              { label: "Ads", onClick: () => navigate("/ads/creatives") },
              { label: "Performance", active: true },
            ]}
          />
        </div>

        {/* Header row */}
        <div className="ad-perf__header-row">
          <div className="ad-perf__title-block">
            <h1 className="ad-perf__title">Ad Performance</h1>
            <p className="ad-perf__subtitle">
              Analytics for ad <strong>{adId}</strong>
            </p>
          </div>

          {/* Date range controls */}
          <div className="ad-perf__date-controls">
            <div className="ad-perf__preset-group">
              {(["7d", "30d", "90d"] as Preset[]).map((p) => (
                <button
                  key={p}
                  type="button"
                  className={`ad-perf__preset-btn${preset === p ? " ad-perf__preset-btn--active" : ""}`}
                  onClick={() => handlePreset(p)}
                >
                  {p === "7d" ? "Last 7d" : p === "30d" ? "Last 30d" : "Last 90d"}
                </button>
              ))}
            </div>

            <span className="ad-perf__date-separator">or</span>

            <div className="ad-perf__date-wrapper">
              <input
                type="date"
                className="ad-perf__date-input"
                value={startDate}
                max={endDate}
                onChange={(e) => handleDateChange("start", e.target.value)}
                aria-label="Start date"
              />
              <i className="ph-calendar-blank ad-perf__date-icon" aria-hidden="true" />
            </div>

            <span className="ad-perf__date-separator">—</span>

            <div className="ad-perf__date-wrapper">
              <input
                type="date"
                className="ad-perf__date-input"
                value={endDate}
                min={startDate}
                onChange={(e) => handleDateChange("end", e.target.value)}
                aria-label="End date"
              />
              <i className="ph-calendar-blank ad-perf__date-icon" aria-hidden="true" />
            </div>
          </div>
        </div>

        {/* Stats */}
        {loading ? (
          <ShimmerLoader type="table" rows={4} />
        ) : (
          <>
            <Row className="g-3 mt-2">
              {statCards.map((card) => (
                <Col key={card.title} xs={12} sm={6} lg={4} xl={true}>
                  <StatCard
                    title={card.title}
                    value={card.value}
                    iconClassName={card.iconClassName}
                    iconBgColor={card.iconBgColor}
                    iconColor={card.iconColor}
                  />
                </Col>
              ))}
            </Row>

            {/* Daily breakdown */}
            <div className="ad-perf__daily-card">
              <div className="ad-perf__daily-card-header">
                <h2 className="ad-perf__daily-title">Daily Breakdown</h2>
                {daily.length > 0 && (
                  <span className="ad-perf__daily-count">{daily.length} days</span>
                )}
              </div>

              {daily.length > 0 ? (
                <div className="ads-table-wrapper">
                  <table className="table ad-perf__table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Impressions</th>
                        <th>Clicks</th>
                        <th>CTR</th>
                      </tr>
                    </thead>
                    <tbody>
                      {daily.map((row) => (
                        <tr key={row.date}>
                          <td>{formatDate(row.date)}</td>
                          <td>{formatNum(row.impressions)}</td>
                          <td>{formatNum(row.clicks)}</td>
                          <td>{row.ctr.toFixed(2)}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="ad-perf__empty">No daily data available for the selected date range.</p>
              )}
            </div>
          </>
        )}

        {error && (
          <div className="ad-perf__error">
            <Alert variant="danger" dismissible onClose={fetchAnalytics}>
              {error}
            </Alert>
          </div>
        )}
      </Container>
    </div>
  );
};

export default AdPerformancePage;
